import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Collapse, Container, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import { useNotifier } from "../../../Core/Notifier";
import { API_URL } from "../../../config";

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", cream: "#faf8f4", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenBg: "#edf7ee", greenTx: "#2d6a35", redTx: "#9b2c2c", redBg: "#fef2f2",
  peach: "#ffebd3", inputBg: "#f8f7f5",
};
const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg, border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold }, transition: "all 0.15s", "& fieldset": { border: "none" } },
  "& .MuiInputLabel-root": { fontSize: 13 }, "& input": { py: "9px" },
};

const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` });
const api = {
  get:  (p: string) => fetch(`${API_URL}${p}`, { headers: hdrs() }).then(r => r.json()),
  post: (p: string, b: any) => fetch(`${API_URL}${p}`, { method: "POST", headers: hdrs(), body: JSON.stringify(b) }).then(r => r.json()),
  del:  (p: string) => fetch(`${API_URL}${p}`, { method: "DELETE", headers: hdrs() }).then(r => r.json()),
};

const DEFAULT_COLORS = ["#8b5cf6", "#ec4899", "#f59e0b", "#3b82f6", "#10b981", "#ef4444", "#06b6d4", "#f97316"];
const PRICING_TYPES = [
  { value: "per_event", label: "Per Event" },
  { value: "per_hour", label: "Per Hour" },
  { value: "per_unit", label: "Per Unit" },
  { value: "per_plate", label: "Per Plate" },
];
const fmtCAD = (n: number) => n.toLocaleString("en-CA", { style: "currency", currency: "CAD" });

// Count all items in a tree node recursively
const countItems = (node: any): number => {
  let count = 0;
  // Items directly under categories
  (node.categories ?? []).forEach((cat: any) => {
    count += (cat.items ?? []).length;
    (cat.subcategories ?? []).forEach((sub: any) => {
      count += (sub.items ?? []).length;
    });
  });
  // Direct items
  count += (node.items ?? []).length;
  return count;
};

const countVendors = (node: any): number => {
  let total = 0;
  const countForItems = (items: any[]) => items.reduce((s: number, i: any) => s + (i.vendor_prices?.length ?? 0), 0);
  (node.categories ?? []).forEach((cat: any) => {
    total += countForItems(cat.items ?? []);
    (cat.subcategories ?? []).forEach((sub: any) => { total += countForItems(sub.items ?? []); });
  });
  total += countForItems(node.items ?? []);
  return total;
};

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR PRICE MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const VendorPriceModal: React.FC<{
  open: boolean; itemId: number; itemName: string;
  vendors: any[]; existingPrices: any[];
  onClose: () => void; onSaved: () => void;
}> = ({ open, itemId, itemName, vendors, existingPrices, onClose, onSaved }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [vendorId, setVendorId] = useState("");
  const [pricingType, setPricingType] = useState("per_event");
  const [price, setPrice] = useState("0");
  const [isAdjustable, setIsAdjustable] = useState(false);
  const [minPrice, setMinPrice] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  const assignedVendorIds = existingPrices.map((p: any) => p.vendor_id);

  const handleSave = async () => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/item-vendor", {
      item_id: itemId, vendor_id: Number(vendorId), pricing_type: pricingType,
      price: Number(price), is_adjustable: isAdjustable,
      min_price_limit: minPrice ? Number(minPrice) : null, is_default: isDefault,
    });
    if (res.code === 200) { showMessage(res.message); onSaved(); onClose(); }
    else showErrorMessage(res.message);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.white }}>Add Vendor Price — {itemName}</Typography>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField select label="Vendor" size="small" value={vendorId} onChange={e => setVendorId(e.target.value)} sx={inputSx}>
          <MenuItem value="" disabled>Select vendor...</MenuItem>
          {vendors.filter(v => !assignedVendorIds.includes(v.id)).map((v: any) => (
            <MenuItem key={v.id} value={v.id}>{v.name} {v.company_name ? `(${v.company_name})` : ""}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField select label="Type" size="small" value={pricingType} onChange={e => setPricingType(e.target.value)} sx={{ ...inputSx, flex: 1 }}>
            {PRICING_TYPES.map(pt => <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>)}
          </TextField>
          <TextField label="Price" type="number" size="small" value={price} onChange={e => setPrice(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ ...inputSx, flex: 1 }} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip title={isAdjustable ? "Adjustable (can be discounted)" : "Fixed price"}>
            <Button size="small" startIcon={isAdjustable ? <LockOpenOutlinedIcon sx={{ fontSize: 14 }} /> : <LockOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setIsAdjustable(!isAdjustable)}
              sx={{ textTransform: "none", fontSize: 11, color: isAdjustable ? T.gold : T.slateLt }}>
              {isAdjustable ? "Adjustable" : "Fixed"}
            </Button>
          </Tooltip>
          <Tooltip title="Set as default vendor for this item">
            <Button size="small" startIcon={isDefault ? <StarIcon sx={{ fontSize: 14 }} /> : <StarBorderIcon sx={{ fontSize: 14 }} />}
              onClick={() => setIsDefault(!isDefault)}
              sx={{ textTransform: "none", fontSize: 11, color: isDefault ? T.gold : T.slateLt }}>
              {isDefault ? "Default" : "Not Default"}
            </Button>
          </Tooltip>
        </Box>
        {isAdjustable && (
          <TextField label="Minimum Price Limit" type="number" size="small" value={minPrice} onChange={e => setMinPrice(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={inputSx} />
        )}
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !vendorId}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "..." : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ITEM ROW
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemRow: React.FC<{
  item: any; onEdit: (item: any) => void; onDelete: (item: any) => void;
  onManageVendors: (item: any) => void; onSetDefault: (itemId: number, vpId: number) => void;
  onRemoveVendor: (vpId: number) => void;
}> = ({ item, onEdit, onDelete, onManageVendors, onSetDefault, onRemoveVendor }) => {
  const defVendor = item.default_vendor_price;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.8, borderBottom: `1px solid ${T.line}20`, "&:hover .item-actions": { opacity: 1 } }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: item.status === "active" ? T.greenTx : T.slateLt, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.navy, flex: 1 }}>{item.name}</Typography>
      {item.unit && <Chip label={item.unit} size="small" sx={{ height: 16, fontSize: 8, bgcolor: T.bg, color: T.slate }} />}
      {item.default_price != null && <Chip label={fmtCAD(Number(item.default_price))} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />}
      {defVendor && (
        <Chip icon={<StarIcon sx={{ fontSize: 10, color: `${T.gold} !important` }} />}
          label={`${defVendor.vendor?.name ?? "Vendor"} — ${fmtCAD(Number(defVendor.price))}`}
          size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: `${T.gold}12`, color: T.gold, "& .MuiChip-icon": { ml: "3px" } }} />
      )}
      <Chip label={`${(item.vendor_prices ?? []).length} vendors`} size="small" sx={{ height: 18, fontSize: 9, bgcolor: T.bg, color: T.slate }} />
      <Box className="item-actions" sx={{ opacity: 0, transition: "opacity 0.15s", display: "flex", gap: 0.3 }}>
        <Tooltip title="Manage Vendors"><IconButton size="small" onClick={() => onManageVendors(item)} sx={{ color: T.gold }}><StorefrontOutlinedIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip>
        <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(item)} sx={{ color: T.slateLt }}><EditOutlinedIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(item)} sx={{ color: T.slateLt }}><DeleteOutlineIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const BanquetServiceTree: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [tree, setTree] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Inline add/edit states
  const [addTarget, setAddTarget] = useState<{
    level: "service" | "category" | "subcategory" | "item";
    parentId?: number; serviceId?: number; categoryId?: number;
  } | null>(null);
  const [addName, setAddName] = useState("");
  const [addColor, setAddColor] = useState(DEFAULT_COLORS[0]);
  const [editTarget, setEditTarget] = useState<{ level: string; id: number; name: string } | null>(null);
  const [editName, setEditName] = useState("");

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<{ level: string; id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Vendor price modal
  const [priceModal, setPriceModal] = useState<{ itemId: number; itemName: string; existingPrices: any[] } | null>(null);

  const toggle = (key: string) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [tRes, vRes] = await Promise.all([api.get("/banquet/service-tree"), api.get("/banquet/vendors")]);
    if (tRes.code === 200) {
      setTree(tRes.data ?? []);
      const exp: Record<string, boolean> = {};
      (tRes.data ?? []).forEach((s: any) => { exp[`svc-${s.id}`] = true; });
      setExpanded(exp);
    }
    if (vRes.code === 200) setVendors(vRes.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Add handlers ──
  const handleAdd = async () => {
    if (!addTarget || !addName.trim()) return;
    setSaving(true);
    let res;
    if (addTarget.level === "service") {
      res = await api.post("/banquet/service-tree/service", { name: addName.trim(), color: addColor });
    } else if (addTarget.level === "category") {
      res = await api.post("/banquet/service-tree/category", { service_id: addTarget.parentId, name: addName.trim() });
    } else if (addTarget.level === "subcategory") {
      res = await api.post("/banquet/service-tree/subcategory", { service_id: addTarget.serviceId, category_id: addTarget.parentId, name: addName.trim() });
    } else {
      res = await api.post("/banquet/service-tree/item", {
        service_id: addTarget.serviceId, category_id: addTarget.categoryId || null,
        subcategory_id: addTarget.parentId || null, name: addName.trim(),
      });
    }
    if (res?.code === 200) { showMessage(res.message); fetchAll(); }
    else showErrorMessage(res?.message || "Failed");
    setSaving(false);
    setAddTarget(null);
    setAddName("");
  };

  // ── Edit handler ──
  const handleEdit = async () => {
    if (!editTarget || !editName.trim()) return;
    setSaving(true);
    const res = await api.post(`/banquet/service-tree/${editTarget.level}`, { id: editTarget.id, name: editName.trim(), ...(editTarget.level === "category" ? { service_id: 0 } : {}), ...(editTarget.level === "subcategory" ? { service_id: 0, category_id: 0 } : {}), ...(editTarget.level === "item" ? { service_id: 0 } : {}) });
    if (res?.code === 200) { showMessage("Updated"); fetchAll(); }
    else showErrorMessage(res?.message || "Failed");
    setSaving(false);
    setEditTarget(null);
  };

  // ── Delete handler ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await api.del(`/banquet/service-tree/${deleteTarget.level}/${deleteTarget.id}`);
    if (res?.code === 200) { showMessage(res.message); fetchAll(); }
    else showErrorMessage(res?.message || "Failed");
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  // ── Set default vendor ──
  const handleSetDefault = async (itemId: number, vendorPriceId: number) => {
    const res = await api.post(`/banquet/service-tree/item/${itemId}/set-default`, { vendor_price_id: vendorPriceId });
    if (res?.code === 200) { showMessage("Default set"); fetchAll(); }
  };

  // ── Remove vendor price ──
  const handleRemoveVendorPrice = async (id: number) => {
    const res = await api.del(`/banquet/service-tree/item-vendor/${id}`);
    if (res?.code === 200) { showMessage("Removed"); fetchAll(); }
  };

  // ── Inline add row ──
  const InlineAdd: React.FC<{ level: string }> = ({ level }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      <TextField size="small" placeholder={`New ${level} name...`} value={addName} onChange={e => setAddName(e.target.value)}
        autoFocus onKeyDown={e => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") setAddTarget(null); }}
        sx={{ ...inputSx, flex: 1 }} />
      {addTarget?.level === "service" && (
        <Box sx={{ display: "flex", gap: 0.3 }}>
          {DEFAULT_COLORS.map(c => (
            <Box key={c} onClick={() => setAddColor(c)} sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: c, cursor: "pointer", border: addColor === c ? `2px solid ${T.navy}` : "2px solid transparent" }} />
          ))}
        </Box>
      )}
      <Button size="small" onClick={handleAdd} disabled={saving || !addName.trim()}
        sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, color: T.gold, minWidth: 0 }}>
        {saving ? <CircularProgress size={12} /> : "Save"}
      </Button>
      <Button size="small" onClick={() => setAddTarget(null)} sx={{ textTransform: "none", fontSize: 11, color: T.slateLt, minWidth: 0 }}>Cancel</Button>
    </Box>
  );

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>;

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, pt: 1 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>Service Catalogue</Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>Manage services, categories, subcategories, items, and vendor pricing</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setAddTarget({ level: "service" }); setAddName(""); setAddColor(DEFAULT_COLORS[tree.length % DEFAULT_COLORS.length]); }}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 2.5, boxShadow: "none", "&:hover": { bgcolor: "#1a3550" } }}>
          Add Service
        </Button>
      </Box>

      {tree.length === 0 && !addTarget ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <CategoryOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: T.slateLt }}>No services yet. Click "Add Service" to create your first one.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {tree.map((svc: any) => {
            const svcKey = `svc-${svc.id}`;
            const isOpen = expanded[svcKey] ?? false;
            const totalItems = countItems(svc);
            const totalVendorLinks = countVendors(svc);

            return (
              <Paper key={svc.id} elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, overflow: "hidden" }}>
                {/* ── Service header ── */}
                <Box onClick={() => toggle(svcKey)} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, cursor: "pointer", bgcolor: T.white, "&:hover": { bgcolor: `${T.bg}80` }, transition: "background 0.15s" }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: svc.color ?? T.slateLt, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy, flex: 1 }}>{svc.name}</Typography>
                  <Chip label={`${totalItems} items`} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: T.bg, color: T.slate }} />
                  <Chip label={`${totalVendorLinks} vendors`} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: `${svc.color ?? T.slateLt}15`, color: svc.color ?? T.slateLt }} />
                  <Tooltip title="Add Category"><IconButton size="small" onClick={e => { e.stopPropagation(); setAddTarget({ level: "category", parentId: svc.id }); setAddName(""); }} sx={{ color: T.slateLt }}><AddIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={e => { e.stopPropagation(); setEditTarget({ level: "service", id: svc.id, name: svc.name }); setEditName(svc.name); }} sx={{ color: T.slateLt }}><EditOutlinedIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={e => { e.stopPropagation(); setDeleteTarget({ level: "service", id: svc.id, name: svc.name }); }} sx={{ color: T.slateLt }}><DeleteOutlineIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                  {isOpen ? <ExpandLessIcon sx={{ fontSize: 18, color: T.slateLt }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: T.slateLt }} />}
                </Box>

                <Collapse in={isOpen}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    {/* Add category inline */}
                    {addTarget?.level === "category" && addTarget.parentId === svc.id && <InlineAdd level="category" />}

                    {/* ── Categories ── */}
                    {(svc.categories ?? []).map((cat: any) => {
                      const catKey = `cat-${cat.id}`;
                      const catOpen = expanded[catKey] ?? true;
                      const catItemCount = (cat.items ?? []).length + (cat.subcategories ?? []).flatMap((s: any) => s.items ?? []).length;
                      return (
                        <Box key={cat.id} sx={{ ml: 2, mt: 1 }}>
                          <Box onClick={() => toggle(catKey)} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, cursor: "pointer", "&:hover .cat-actions": { opacity: 1 } }}>
                            <CategoryOutlinedIcon sx={{ fontSize: 14, color: svc.color ?? T.slateLt }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy, flex: 1 }}>{cat.name}</Typography>
                            <Chip label={catItemCount} size="small" sx={{ height: 16, fontSize: 9, bgcolor: T.bg, color: T.slate }} />
                            <Box className="cat-actions" sx={{ opacity: 0, transition: "opacity 0.15s", display: "flex", gap: 0.3 }}>
                              <Tooltip title="Add Subcategory"><IconButton size="small" onClick={e => { e.stopPropagation(); setAddTarget({ level: "subcategory", parentId: cat.id, serviceId: svc.id }); setAddName(""); }} sx={{ color: T.slateLt }}><AddIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
                              <Tooltip title="Add Item"><IconButton size="small" onClick={e => { e.stopPropagation(); setAddTarget({ level: "item", parentId: undefined, serviceId: svc.id, categoryId: cat.id }); setAddName(""); }} sx={{ color: T.slateLt }}><AddIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
                              <Tooltip title="Edit"><IconButton size="small" onClick={e => { e.stopPropagation(); setEditTarget({ level: "category", id: cat.id, name: cat.name }); setEditName(cat.name); }} sx={{ color: T.slateLt }}><EditOutlinedIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
                              <Tooltip title="Delete"><IconButton size="small" onClick={e => { e.stopPropagation(); setDeleteTarget({ level: "category", id: cat.id, name: cat.name }); }} sx={{ color: T.slateLt }}><DeleteOutlineIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
                            </Box>
                            {catOpen ? <ExpandLessIcon sx={{ fontSize: 14, color: T.slateLt }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: T.slateLt }} />}
                          </Box>

                          <Collapse in={catOpen}>
                            <Box sx={{ ml: 2.5 }}>
                              {/* Add subcategory inline */}
                              {addTarget?.level === "subcategory" && addTarget.parentId === cat.id && <InlineAdd level="subcategory" />}

                              {/* ── Subcategories ── */}
                              {(cat.subcategories ?? []).map((sub: any) => {
                                const subKey = `sub-${sub.id}`;
                                const subOpen = expanded[subKey] ?? true;
                                return (
                                  <Box key={sub.id} sx={{ mt: 0.5 }}>
                                    <Box onClick={() => toggle(subKey)} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4, cursor: "pointer", "&:hover .sub-actions": { opacity: 1 } }}>
                                      <FolderOpenOutlinedIcon sx={{ fontSize: 13, color: T.gold }} />
                                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slate, flex: 1 }}>{sub.name}</Typography>
                                      <Chip label={(sub.items ?? []).length} size="small" sx={{ height: 14, fontSize: 8, bgcolor: T.bg, color: T.slate }} />
                                      <Box className="sub-actions" sx={{ opacity: 0, transition: "opacity 0.15s", display: "flex", gap: 0.3 }}>
                                        <Tooltip title="Add Item"><IconButton size="small" onClick={e => { e.stopPropagation(); setAddTarget({ level: "item", parentId: sub.id, serviceId: svc.id, categoryId: cat.id }); setAddName(""); }} sx={{ color: T.slateLt }}><AddIcon sx={{ fontSize: 11 }} /></IconButton></Tooltip>
                                        <Tooltip title="Edit"><IconButton size="small" onClick={e => { e.stopPropagation(); setEditTarget({ level: "subcategory", id: sub.id, name: sub.name }); setEditName(sub.name); }} sx={{ color: T.slateLt }}><EditOutlinedIcon sx={{ fontSize: 11 }} /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" onClick={e => { e.stopPropagation(); setDeleteTarget({ level: "subcategory", id: sub.id, name: sub.name }); }} sx={{ color: T.slateLt }}><DeleteOutlineIcon sx={{ fontSize: 11 }} /></IconButton></Tooltip>
                                      </Box>
                                      {subOpen ? <ExpandLessIcon sx={{ fontSize: 12, color: T.slateLt }} /> : <ExpandMoreIcon sx={{ fontSize: 12, color: T.slateLt }} />}
                                    </Box>
                                    <Collapse in={subOpen}>
                                      <Box sx={{ ml: 2 }}>
                                        {addTarget?.level === "item" && addTarget.parentId === sub.id && <InlineAdd level="item" />}
                                        {(sub.items ?? []).map((item: any) => (
                                          <ItemRow key={item.id} item={item}
                                            onEdit={i => { setEditTarget({ level: "item", id: i.id, name: i.name }); setEditName(i.name); }}
                                            onDelete={i => setDeleteTarget({ level: "item", id: i.id, name: i.name })}
                                            onManageVendors={i => setPriceModal({ itemId: i.id, itemName: i.name, existingPrices: i.vendor_prices ?? [] })}
                                            onSetDefault={handleSetDefault} onRemoveVendor={handleRemoveVendorPrice} />
                                        ))}
                                      </Box>
                                    </Collapse>
                                  </Box>
                                );
                              })}

                              {/* Items directly under category (no subcategory) */}
                              {addTarget?.level === "item" && addTarget.categoryId === cat.id && !addTarget.parentId && <InlineAdd level="item" />}
                              {(cat.items ?? []).map((item: any) => (
                                <ItemRow key={item.id} item={item}
                                  onEdit={i => { setEditTarget({ level: "item", id: i.id, name: i.name }); setEditName(i.name); }}
                                  onDelete={i => setDeleteTarget({ level: "item", id: i.id, name: i.name })}
                                  onManageVendors={i => setPriceModal({ itemId: i.id, itemName: i.name, existingPrices: i.vendor_prices ?? [] })}
                                  onSetDefault={handleSetDefault} onRemoveVendor={handleRemoveVendorPrice} />
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}

                    {/* ── Uncategorized items (directly under service) ── */}
                    {(svc.items ?? []).length > 0 && (
                      <Box sx={{ ml: 2, mt: 1 }}>
                        <Typography sx={{ fontSize: 11, color: T.slateLt, fontStyle: "italic", mb: 0.5 }}>Uncategorized</Typography>
                        {(svc.items ?? []).map((item: any) => (
                          <ItemRow key={item.id} item={item}
                            onEdit={i => { setEditTarget({ level: "item", id: i.id, name: i.name }); setEditName(i.name); }}
                            onDelete={i => setDeleteTarget({ level: "item", id: i.id, name: i.name })}
                            onManageVendors={i => setPriceModal({ itemId: i.id, itemName: i.name, existingPrices: i.vendor_prices ?? [] })}
                            onSetDefault={handleSetDefault} onRemoveVendor={handleRemoveVendorPrice} />
                        ))}
                      </Box>
                    )}

                    {/* Add item directly to service (no category) */}
                    <Box sx={{ mt: 1, ml: 2 }}>
                      <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                        onClick={() => { setAddTarget({ level: "item", parentId: undefined, serviceId: svc.id, categoryId: undefined }); setAddName(""); }}
                        sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, color: T.slateLt, "&:hover": { color: T.gold } }}>
                        Add Item
                      </Button>
                      {addTarget?.level === "item" && addTarget.serviceId === svc.id && !addTarget.categoryId && !addTarget.parentId && <InlineAdd level="item" />}
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}

          {/* Add new service inline */}
          {addTarget?.level === "service" && (
            <Paper elevation={0} sx={{ p: 2, borderRadius: "14px", border: `1.5px solid ${T.gold}` }}>
              <InlineAdd level="service" />
            </Paper>
          )}
        </Box>
      )}

      {/* Edit name dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontSize: 16 }}>Edit {editTarget?.level}</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Name" value={editName} onChange={e => setEditName(e.target.value)} autoFocus
            onKeyDown={e => { if (e.key === "Enter") handleEdit(); }} sx={{ ...inputSx, mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600, bgcolor: T.navy, borderRadius: "8px", "&:hover": { bgcolor: "#1a3550" } }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}><WarningAmberIcon color="error" /> Delete {deleteTarget?.level}?</DialogTitle>
        <DialogContent><DialogContentText sx={{ fontSize: 13 }}>Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{isDeleting ? "..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>

      {/* Vendor price modal */}
      {priceModal && (
        <VendorPriceModal
          open={true} itemId={priceModal.itemId} itemName={priceModal.itemName}
          vendors={vendors} existingPrices={priceModal.existingPrices}
          onClose={() => setPriceModal(null)} onSaved={fetchAll}
        />
      )}
    </Container>
  );
};

export default BanquetServiceTree;
